/**
 * Single Source of Truth Display Layer (client mirror of server/src/utils/displayTime.js)
 *
 * ALL user-facing time display goes through this layer.
 * Handles the current storage reality (Paris time stored as "UTC" / "+00")
 * and provides consistent conversion to any display timezone.
 *
 * THE PROBLEM:
 * - Binom returns "2026-01-19 09:35:00" (Paris time)
 * - We store it as "2026-01-19T09:35:00.000Z" (claiming UTC)
 * - Actual UTC was 08:35:00
 * - Naive `new Date(stored).toLocaleString(..., {timeZone:'Europe/Paris'})`
 *   double-converts → shows 10:35 (WRONG)
 *
 * THE SOLUTION:
 * - storageTimezone = 'Europe/Paris' (the ACTUAL timezone of stored data)
 * - When converting to display, we know the true origin; no double conversion
 *
 * MIGRATION PATH:
 * - When storage is fixed to true UTC, change storageTimezone to 'UTC'
 * - All display code continues to work correctly
 *
 * Keep this file in lock-step with server/src/utils/displayTime.js.
 */

const CONFIG = {
  storageTimezone: 'Europe/Paris',
  defaultDisplayTimezone: 'Europe/Paris',
  sourceTimezones: {
    binom: 'Europe/Paris',
    dialics: 'Europe/Paris',
    facebook: 'UTC',
    manual: 'UTC',
  }
}

function parseStoredTimestamp(storedTimestamp, source = null) {
  if (!storedTimestamp) return null

  const actualTimezone = source
    ? (CONFIG.sourceTimezones[source] || CONFIG.storageTimezone)
    : CONFIG.storageTimezone

  if (actualTimezone === 'UTC') {
    return new Date(storedTimestamp)
  }

  let dateStr = typeof storedTimestamp === 'string' ? storedTimestamp : storedTimestamp?.toISOString?.()
  if (!dateStr) return null

  if (dateStr.endsWith('Z')) dateStr = dateStr.slice(0, -1)
  if (dateStr.match(/[+-]\d{2}:\d{2}$/)) dateStr = dateStr.slice(0, -6)
  if (dateStr.includes(' ') && !dateStr.includes('T')) dateStr = dateStr.replace(' ', 'T')

  const offsetHours = getTimezoneOffset(actualTimezone, new Date(dateStr + 'Z'))
  const localDate = new Date(dateStr + 'Z')
  return new Date(localDate.getTime() - offsetHours * 60 * 60 * 1000)
}

function getTimezoneOffset(timezone, date) {
  const formatter = new Intl.DateTimeFormat('en-US', {
    timeZone: timezone,
    timeZoneName: 'shortOffset'
  })
  const parts = formatter.formatToParts(date)
  const offsetPart = parts.find(p => p.type === 'timeZoneName')
  if (!offsetPart) return 0
  const match = offsetPart.value.match(/GMT([+-])(\d+)/)
  if (!match) return 0
  const sign = match[1] === '+' ? 1 : -1
  return sign * parseInt(match[2], 10)
}

function utcToDisplay(utcDate, displayTimezone) {
  const formatter = new Intl.DateTimeFormat('en-US', {
    timeZone: displayTimezone,
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', second: '2-digit',
    hour12: false
  })
  const parts = formatter.formatToParts(utcDate)
  const get = (type) => parts.find(p => p.type === type)?.value
  return {
    year: get('year'),
    month: get('month'),
    day: get('day'),
    hour: parseInt(get('hour'), 10),
    minute: parseInt(get('minute'), 10),
    second: parseInt(get('second'), 10),
    dateStr: `${get('year')}-${get('month')}-${get('day')}`,
    timeStr: `${get('hour')}:${get('minute')}:${get('second')}`
  }
}

export const DisplayTime = {
  getConfig() { return { ...CONFIG } },

  setStorageTimezone(timezone) { CONFIG.storageTimezone = timezone },

  format(dbTimestamp, displayTz = CONFIG.defaultDisplayTimezone, format = 'datetime', source = null) {
    if (!dbTimestamp) return ''
    const utcDate = parseStoredTimestamp(dbTimestamp, source)
    if (!utcDate || isNaN(utcDate.getTime())) return ''
    const display = utcToDisplay(utcDate, displayTz)
    switch (format) {
      case 'datetime': return `${display.dateStr} ${display.timeStr}`
      case 'date': return display.dateStr
      case 'time': return display.timeStr
      case 'hour': return String(display.hour).padStart(2, '0')
      case 'iso': return utcDate.toISOString()
      default: return `${display.dateStr} ${display.timeStr}`
    }
  },

  getHourBucket(dbTimestamp, displayTz = CONFIG.defaultDisplayTimezone, source = null) {
    if (!dbTimestamp) return null
    if (displayTz === CONFIG.storageTimezone && !source) {
      const str = typeof dbTimestamp === 'string' ? dbTimestamp : dbTimestamp?.toISOString?.()
      if (!str) return null
      const match = str.match(/T(\d{2}):/)
      if (match) return parseInt(match[1], 10)
      const spaceMatch = str.match(/ (\d{2}):/)
      if (spaceMatch) return parseInt(spaceMatch[1], 10)
    }
    const utcDate = parseStoredTimestamp(dbTimestamp, source)
    if (!utcDate || isNaN(utcDate.getTime())) return null
    return utcToDisplay(utcDate, displayTz).hour
  },

  getDateString(dbTimestamp, displayTz = CONFIG.defaultDisplayTimezone) {
    return this.format(dbTimestamp, displayTz, 'date')
  },

  aggregateByHour(records, timestampField = 'timestamp', countField = null, displayTz = CONFIG.defaultDisplayTimezone) {
    const hourBuckets = {}
    for (let h = 0; h < 24; h++) hourBuckets[h] = 0
    for (const record of records) {
      const ts = record[timestampField]
      const hour = this.getHourBucket(ts, displayTz)
      if (hour !== null && hour >= 0 && hour < 24) {
        if (countField && record[countField] !== undefined) {
          hourBuckets[hour] += Number(record[countField]) || 0
        } else {
          hourBuckets[hour]++
        }
      }
    }
    return hourBuckets
  },

  hourBucketsToArray(hourBuckets, startHour = 0, endHour = 23) {
    const result = []
    for (let h = startHour; h <= endHour; h++) {
      result.push({
        hour: h,
        label: `${String(h).padStart(2, '0')}:00`,
        count: hourBuckets[h] || 0
      })
    }
    return result
  },

  toUTCDate(dbTimestamp, source = null) {
    return parseStoredTimestamp(dbTimestamp, source)
  },

  diffMinutes(timestamp1, timestamp2) {
    const d1 = new Date(timestamp1)
    const d2 = new Date(timestamp2)
    return Math.round((d2 - d1) / 60000)
  },

  isStorageUTC() { return CONFIG.storageTimezone === 'UTC' },

  // ─── Client helpers ──────────────────────────────────────────────
  // Things DateRangeContext + widgets need but the server doesn't.

  /**
   * YYYY-MM-DD for a Date as seen from Paris wall-clock.
   * Replaces every `foo.toISOString().split('T')[0]` in the client.
   */
  parisDateStr(date = new Date(), tz = CONFIG.defaultDisplayTimezone) {
    return new Intl.DateTimeFormat('en-CA', {
      timeZone: tz,
      year: 'numeric', month: '2-digit', day: '2-digit'
    }).format(date)
  },

  /** Today's YYYY-MM-DD in Paris. */
  nowParisDateStr(tz = CONFIG.defaultDisplayTimezone) {
    return this.parisDateStr(new Date(), tz)
  },

  /**
   * Current hour (0-23) as seen from Paris wall-clock.
   * Replaces `new Date().getHours()` in widgets — that would leak
   * the browser's local zone into "is it peak hour?" logic.
   */
  parisHour(date = new Date(), tz = CONFIG.defaultDisplayTimezone) {
    return parseInt(new Intl.DateTimeFormat('en-GB', {
      timeZone: tz,
      hour: '2-digit',
      hour12: false
    }).format(date), 10)
  },

  /** Shorthand: Paris hour for "right now". */
  nowParisHour(tz = CONFIG.defaultDisplayTimezone) {
    return this.parisHour(new Date(), tz)
  },

  /**
   * Paris-midnight UTC instant for the Paris day that contains `date`.
   * Returned as a Date; compare with any other Date using `>=` / `<`.
   * Replaces `today.setHours(0,0,0,0)` which snaps to browser-local midnight.
   */
  parisMidnight(date = new Date(), tz = CONFIG.defaultDisplayTimezone) {
    const ymd = this.parisDateStr(date, tz)
    const { startIso } = this.parisDayBounds(ymd, tz)
    return new Date(startIso)
  },

  /**
   * Shift a YYYY-MM-DD by N days (calendar math, Paris-aware).
   * Returns YYYY-MM-DD.
   */
  shiftParisDate(dateStr, days, tz = CONFIG.defaultDisplayTimezone) {
    const [y, m, d] = dateStr.split('-').map(Number)
    const probe = new Date(Date.UTC(y, m - 1, d, 12, 0, 0))
    probe.setUTCDate(probe.getUTCDate() + days)
    return this.parisDateStr(probe, tz)
  },

  /**
   * UTC-instant bounds for a Paris day, usable in Supabase `.gte/.lt()` calls.
   * Returns { startIso, endIso } — end is EXCLUSIVE (next-day midnight Paris).
   */
  parisDayBounds(dateStr, tz = CONFIG.defaultDisplayTimezone) {
    const [y, m, d] = dateStr.split('-').map(Number)
    const probeStart = new Date(Date.UTC(y, m - 1, d, 0, 0, 0))
    const startOffset = getTimezoneOffset(tz, probeStart)
    const startIso = new Date(probeStart.getTime() - startOffset * 3600 * 1000).toISOString()

    const probeEnd = new Date(Date.UTC(y, m - 1, d + 1, 0, 0, 0))
    const endOffset = getTimezoneOffset(tz, probeEnd)
    const endIso = new Date(probeEnd.getTime() - endOffset * 3600 * 1000).toISOString()

    return { startIso, endIso }
  }
}

export { CONFIG }
export default DisplayTime
